import axios from "axios";

interface Options {
    url: string;
    headers: { [header: string]: string | number | null };
    eTag?: string;
}
let pollInFlight: boolean;
let quickTimeoutId: ReturnType<typeof setTimeout> | undefined;
let intervalId: ReturnType<typeof setTimeout> | undefined;
let failures: number = 0;
const CancelToken = axios.CancelToken;
// tslint:disable-next-line:no-empty
let cancel: () => void = () => {};

export async function start<T>(
    { headers, url, eTag }: Options,
    onSuccess: (result: T, eTag: string) => void,
    onError: (statusCode: number) => void
) {
    if (intervalId) {
        return;
    }

    let resp;

    async function poll() {
        try {
            pollInFlight = true;
            resp = await axios.get<T>(url, {
                headers,
                cancelToken: new CancelToken(function executor(c) {
                    // An executor function receives a cancel function as a parameter
                    cancel = c;
                }),
            });
            failures = 0;
            onSuccess(resp.data, resp.headers.etag?.replace("W/", ""));
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }

            if (error?.response?.status !== 304) {
                failures++;
            }

            if (failures > 5) {
                failures = 0;
                onError(error?.response?.status);
            }
        } finally {
            pollInFlight = false;
        }
    }

    if (!eTag) {
        quickTimeoutId = setTimeout(async () => {
            headers["If-None-Match"] = "";
            // short circuit if we're waiting on a promise already
            if (pollInFlight) return;
            await poll();
        }, 500);
        return;
    }

    intervalId = setInterval(async () => {
        headers["If-None-Match"] = eTag;
        // short circuit if we're waiting on a promise already
        if (pollInFlight) return;
        await poll();
    }, 3000);
}

export function stop() {
    if (quickTimeoutId) {
        cancel();
        clearTimeout(quickTimeoutId);
        quickTimeoutId = undefined;
    }
    if (intervalId) {
        cancel();
        clearInterval(intervalId);
        intervalId = undefined;
    }
}

export function isPolling(): boolean {
    return !!intervalId || !!quickTimeoutId;
}
